import React, { useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens, getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm4_u3_x2_beschattung';

const TEXT_BLOCKS = [
  { key: 'block_titel', heading: 'MSR Funktionsbeschreibung: Raum-Beschattungsregelung', body: '' },
  { key: 'block_intro', heading: 'Einführung: Warum Beschattung wichtig ist', body: 'Effektive Beschattungsregelung schützt vor sommerlicher Überhitzung, reduziert Blendung und nutzt im Winter solare Gewinne zur Heizkostenreduktion. Komfort + Energieeffizienz im Zusammenspiel.' },
  { key: 'block_typen', heading: 'Typen von Beschattungselementen', body: '• Außenliegend: Jalousien, Raffstoren, Markisen, Rollläden (maximaler Wärmeschutz).\n• Innenliegend: Lamellen, Rollos, Plissees (Blend-/Sichtschutz).\n• Zwischenglasliegend: Im Scheibenzwischenraum – wartungsarm, mittlere Wirkung.' },
  { key: 'block_manuell', heading: '1. Konventionelle manuelle Bedienung', body: 'Direkter Nutzer-Taster oder mechanische Bedienung (Gurt, Kurbel). Einfach & günstig, aber keine automatische Optimierung – oft energetisch suboptimal.' },
  { key: 'block_zeit', heading: '2. Zeitgesteuerte Automatik', body: 'Vordefinierte Zeiten für Hoch/Runter (morgens hoch, abends runter). Komfort & Anwesenheitssimulation – reagiert nicht auf aktuelle Sonneneinstrahlung.' },
  { key: 'block_antriebe', heading: 'Beschattungsantriebe (Motoren)', body: 'Rohrmotoren für Auf/Ab, ggf. Lamellenverstellung. Ansteuerung konventionell (230V Richtungslogik) oder per Bus (KNX, SMI). Endlagenerkennung, Hinderniserkennung bei modernen Motoren.' },
  { key: 'block_bedien', heading: 'Bedienelemente', body: '• Jalousietaster / Schlüsselschalter\n• Zentrale Panels / Touch\n• Mobile Apps für Fernzugriff & Szenen' },
  { key: 'block_sensoren_basis', heading: 'Sensoren (Basis)', body: '• Sonnensensor / Globalstrahlung: Schwellwertgesteuertes Herunterfahren bei hoher Einstrahlung.\n• Dämmerungssensor: Automatische Schließung bei Nacht (Sicht- & Dämmfunktion).' },
  { key: 'block_sensoren_sicherheit', heading: 'Sensoren (Sicherheit)', body: '• Windwächter: Vorrangige Sicherheitsfahrt hoch bei Sturm.\n• Regensensor: Schutz Markisen / textile Behänge.\n• Frostsensor: Verhindert Festfrieren in Endlagen.' },
  { key: 'block_aktoren', heading: 'Aktoren & Steuerungen', body: '• Jalousieaktoren: Richtungsansteuerung & Lamellenwinkel.\n• Wetterzentrale: Auswertung Sonne, Wind, Regen, Temperatur.\n• Gebäudeautomation (DDC/GA): Fassadenlogik, Prioritäten, Szenen & Integration mit HLK.' },
  { key: 'block_strategie_sonne', heading: 'Strategie: Sonnenautomatik', body: 'Helligkeitsschwelle überschritten → Beschattung runter (Blend-/Hitzeschutz). Bei Unterschreitung + Hysterese wieder hoch für Tageslichtnutzung.' },
  { key: 'block_lamellen', heading: 'Strategie: Lamellennachführung', body: 'Automatische Anpassung des Lamellenwinkels nach Sonnenstand (Azimut/Elevation). Direkte Strahlung sperren, diffuses Licht lenken → Reduktion Kunstlicht + Kühllast.' },
  { key: 'block_jahreszeit', heading: 'Strategie: Jahreszeitenabhängige Anpassung', body: 'Sommer: Fokus Hitzeschutz. Winter: Maximale passive Gewinne (Beschattung oben bei schwacher Einstrahlung). Unterschiedliche Schwellwerte & Verzögerungen.' },
  { key: 'block_parameter', heading: 'Wichtige Parameter', body: '• Helligkeitsschwellwerte (getrennt Auf/Ab)\n• Windschwellwert (Sicherheit)\n• Verzögerungszeiten (Flattern verhindern)\n• Lamellenwinkel / Positionsprofile\n• Zeitprogramme & Prioritätsmatrix' },
  { key: 'block_effizienz', heading: 'Fokus Energieeffizienz', body: 'Außenliegender Sonnenschutz zuerst. Intelligente Lamellensteuerung. Integration mit Heizung/Kühlung/Lüftung. Manuelle Übersteuerung zeitlich begrenzt, Rückfall in Automatik.' },
  { key: 'block_zusammenfassung', heading: 'Zusammenfassung', body: 'Gut parametrierte Beschattungsregelung minimiert Kühlenergie, schützt vor Blendung, steigert Komfort und nutzt passive Gewinne. Kombination aus Automatik + sinnvoller manueller Eingriffsmöglichkeit für Akzeptanz.' }
];

const IMAGE_BLOCKS = [{ key: 'img_0', src: '/assets/drawable/zzz_help.png', label: 'Beschattung (Platzhalter)' }];

const M4_U3_X2_Beschattung: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM4, canUploadImagesM4 } = useAuth();
  const canEditTexts = canEditTextsM4;
  const canUploadImages = canUploadImagesM4;
  const { darkMode } = useTheme();
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = useCallback((key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) s = s.split('/').pop() || s;
    return `/assets/unit_custom/${s}`;
  }, [override]);

  const textBlocks = useMemo(() =>
    TEXT_BLOCKS.map(tb => ({ ...tb, body: override?.texts?.[tb.key] ?? tb.body })),
    [override]
  );

  const images = useMemo(() =>
    IMAGE_BLOCKS.map(img => ({ ...img, src: imgSrc(img.key, img.src) })),
    [imgSrc]
  );

  const fullText = useMemo(() => textBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [textBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      await refresh();
    } catch (e: any) {
      console.error(e);
      alert('Fehler beim Speichern: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      await refresh();
    } catch (e: any) {
      console.error(e);
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : () => speak(), sx: { color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text } }, React.createElement(SpeakerIcon, { style: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '4.3.2 Beschattung', actions: ttsBtn },
    React.createElement(Paper, { sx: getCardSx(darkMode), elevation: 2 },
      React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, images.map(img => React.createElement(ZoomableImage, { key: img.key, src: img.src, label: img.label, onReplace: canUploadImages ? (f: File) => handleImageReplace(img.key, f) : undefined })) ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, textBlocks.map(tb => React.createElement(EditableTextBlock, { key: tb.key, heading: tb.heading, body: tb.body, onSave: canEditTexts ? (v: string) => handleTextSave(tb.key, v) : undefined, pageId: PAGE_ID, blockId: tb.key })) )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m4_u3_x99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M4_U3_X2_Beschattung;

